import {
  Binoculars,
  Cards,
  CaretLeft,
  FilmStrip,
  Hash,
  Image,
  MusicNotes,
  PaperPlaneTilt,
  Plus,
  SlidersHorizontal,
  Sparkle,
  SquaresFour,
  UploadSimple,
  VideoCamera,
} from '@phosphor-icons/react';
import { cn, IconButton } from '@reelplicate/ui';
import type { ComponentType, ReactNode } from 'react';
import { Link as RouterLink, useLocation, useParams } from 'react-router';

import { collectionCovers, collectionItems, type Facet } from './library';
import { formatBytes } from './media-api';
import { useMedia } from './media-context';
import type { Asset, Collection } from './types';

type Icon = ComponentType<{ className?: string }>;

const TYPE_ICONS: Record<string, Icon> = {
  reel: FilmStrip,
  carousel: Cards,
  clip: VideoCamera,
  image: Image,
  audio: MusicNotes,
  caption: Hash,
};
const SOURCE_ICONS: Record<string, Icon> = {
  generated: Sparkle,
  uploaded: UploadSimple,
  saved: Binoculars,
  published: PaperPlaneTilt,
};

const option = (facets: Facet[], facetId: string, value: string) =>
  facets.find((f) => f.id === facetId)!.options.find((o) => o.value === value)!;

const GB = 1024 ** 3;

const row =
  'flex w-full items-center gap-2.5 rounded-md px-2.5 outline-none focus-visible:shadow-focus-ring';

function NavItem({
  to,
  icon: Icon,
  label,
  count,
  active,
}: {
  to: string;
  icon: Icon;
  label: string;
  count: number;
  active: boolean;
}) {
  return (
    <li>
      <RouterLink
        to={to}
        aria-current={active ? 'page' : undefined}
        className={cn(row, 'py-[7px]', active ? 'bg-brand-subtle' : 'hover:bg-bg-subtle')}
      >
        <Icon aria-hidden className="text-icon-secondary size-4 shrink-0" />
        <span className="text-body-sm text-text-primary flex-1 truncate">{label}</span>
        <span className="text-caption text-text-tertiary">{count}</span>
      </RouterLink>
    </li>
  );
}

/** Three overlapping 18×28 covers (Figma "Covers"). */
export function Covers({ assets }: { assets: Asset[] }) {
  const covers = assets.filter((a) => a.thumbnail).slice(0, 3);
  return (
    <span aria-hidden className="flex shrink-0">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className={cn(
            'border-bg-surface bg-bg-emphasis h-7 w-[18px] overflow-hidden rounded-xs border-[1.5px]',
            i < 2 && '-mr-2.5',
          )}
        >
          {covers[i] && (
            <img src={covers[i].thumbnail} alt="" className="size-full object-cover" />
          )}
        </span>
      ))}
    </span>
  );
}

/** Figma "Media / Collection row" (184:1254). */
function CollectionRow({
  collection,
  active,
}: {
  collection: Collection;
  active: boolean;
}) {
  const { assets } = useMedia();
  // Hand-picked: from the loaded library. Smart: the server's count and covers.
  const items = collectionItems(collection, assets);
  const covers = collection.smart ? collectionCovers(collection, assets) : items;
  const meta = collection.smart
    ? collection.smart.broken.length
      ? `${collection.count} · needs fixing`
      : `${collection.count} · ${collection.smart.rule}`
    : `${items.length} ${items.length === 1 ? 'item' : 'items'}`;
  return (
    <li>
      <RouterLink
        to={`/media/collections/${collection.id}`}
        aria-current={active ? 'page' : undefined}
        className={cn(row, 'py-1.5', active ? 'bg-brand-subtle' : 'hover:bg-bg-subtle')}
      >
        <Covers assets={covers} />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="text-body-sm-strong text-text-primary truncate">
            {collection.name}
          </span>
          <span className="text-caption text-text-tertiary truncate">{meta}</span>
        </span>
        {collection.smart && (
          <SlidersHorizontal
            aria-label="Smart collection"
            className="text-icon-secondary size-3.5 shrink-0"
          />
        )}
      </RouterLink>
    </li>
  );
}

function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex items-center gap-1.5 pt-3.5 pb-1 pl-2.5">
      <h3 className="text-overline text-text-tertiary flex-1 uppercase">{children}</h3>
      {action}
    </div>
  );
}

/**
 * Figma "Library" (185:109): browse by type or source, and jump to a
 * collection. The type and source rows filter the library through the URL
 * (`/media?type=reel`), so they can be linked and go back and forward.
 */
export function LibraryPanel({
  onCollapse,
  onNewCollection,
  onNewSmartCollection,
}: {
  onCollapse: () => void;
  onNewCollection: () => void;
  onNewSmartCollection: () => void;
}) {
  const { assets, collections, facets, storage } = useMedia();
  const { pathname, search } = useLocation();
  const { collectionId } = useParams();
  const params = new URLSearchParams(search);
  const onLibrary = pathname === '/media';
  const [type, source] = [params.get('type'), params.get('source')];

  const storageText = `${storage.usedBytes < GB ? formatBytes(storage.usedBytes) : (storage.usedBytes / GB).toFixed(1)} of ${Math.round(storage.limitBytes / GB)} GB`;

  const count = (facetId: string, value: string) =>
    assets.filter(option(facets, facetId, value).matches).length;

  return (
    <nav
      aria-label="Library"
      className="bg-bg-surface border-border-subtle flex h-full w-[260px] shrink-0 flex-col gap-1 overflow-y-auto border-r px-4 pt-6 pb-5"
    >
      <div className="flex items-center gap-2 pb-2">
        <h2 className="text-heading-h4 text-text-primary flex-1">Library</h2>
        <IconButton
          icon={CaretLeft}
          size="sm"
          aria-label="Hide library"
          onClick={onCollapse}
        />
      </div>

      <ul className="flex flex-col gap-1">
        <NavItem
          to="/media"
          icon={SquaresFour}
          label="All media"
          count={assets.length}
          active={onLibrary && !type && !source}
        />
        {Object.entries(TYPE_ICONS).map(([value, icon]) => (
          <NavItem
            key={value}
            to={`/media?type=${value}`}
            icon={icon}
            label={option(facets, 'type', value).label}
            count={count('type', value)}
            active={onLibrary && type === value && !source}
          />
        ))}
      </ul>

      <SectionTitle>Sources</SectionTitle>
      <ul className="flex flex-col gap-1">
        {Object.entries(SOURCE_ICONS).map(([value, icon]) => (
          <NavItem
            key={value}
            to={`/media?source=${value}`}
            icon={icon}
            label={option(facets, 'source', value).label}
            count={count('source', value)}
            active={onLibrary && source === value && !type}
          />
        ))}
      </ul>

      <SectionTitle
        action={
          <IconButton
            icon={Plus}
            size="sm"
            aria-label="New collection"
            onClick={onNewCollection}
            className="-my-2"
          />
        }
      >
        Collections
      </SectionTitle>
      <ul className="flex flex-col gap-1">
        {collections
          .filter((c) => !c.smart)
          .map((c) => (
            <CollectionRow key={c.id} collection={c} active={collectionId === c.id} />
          ))}
      </ul>

      <SectionTitle
        action={
          <IconButton
            icon={Plus}
            size="sm"
            aria-label="New smart collection"
            onClick={onNewSmartCollection}
            className="-my-2"
          />
        }
      >
        Smart collections
      </SectionTitle>
      <ul className="flex flex-col gap-1">
        {collections
          .filter((c) => c.smart)
          .map((c) => (
            <CollectionRow key={c.id} collection={c} active={collectionId === c.id} />
          ))}
      </ul>

      <div className="mt-auto flex flex-col gap-1.5 pt-4">
        <div className="text-caption flex justify-between">
          <span className="text-text-secondary">Storage</span>
          <span className="text-text-primary">{storageText}</span>
        </div>
        <div
          role="meter"
          aria-label="Storage used"
          aria-valuemin={0}
          aria-valuemax={storage.limitBytes}
          aria-valuenow={storage.usedBytes}
          aria-valuetext={storageText}
          className="bg-bg-muted h-1.5 overflow-hidden rounded-full"
        >
          <div
            className="bg-brand-primary h-full rounded-full"
            style={{
              width: `${storage.limitBytes ? Math.min(100, (storage.usedBytes / storage.limitBytes) * 100) : 0}%`,
            }}
          />
        </div>
      </div>
    </nav>
  );
}
